import React, { memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type NativeScrollEvent, type NativeSyntheticEvent, StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { useSelector } from '@xstate5/react';

import { theme, useResponsive } from '@sg/garnish';
import { DressingWeight } from '@sg/graphql-schema';
import {
  CustomizationDetails,
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe-features-customization';

import {
  CustomizationBottomBar,
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetailsCompoundContainer,
  CustomizationFloatingBar,
  CustomizationFloatingControls,
} from '@order/features-customization';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useIsLiquidGlassEnabled } from '../../../../../../hooks';
import { DressingProductImage } from '../../../DressingProductImage';
import {
  CustomizationHeader,
  CustomizationModals,
  CustomizationNutritionCalculator,
} from '../../components';

import type { useCustomizationContent } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsView = memo((props: Props) => {
  const {
    actor,
    customizationContent,
    isOutpost,
    isEditProductMode,
    addressId,
    shouldDisableControls,
    isOutOfStockModalOpen,
    closeOutOfStockModal,
    completeCustomization,
  } = props;

  const isDelivery = Boolean(addressId);
  const dressingDetailsImage = customizationContent?.fields?.dressingDetailsImage?.fields?.file?.url; // prettier-ignore

  const { track } = useTelemetry();

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    product,
    restaurantSlug,
    dressingsDetails,
    canMixDressings,
    hasNoSelectedDressing,
    maxDressingsPortionsNumber,
    activeDressingsPortionsNumber,
    mixedDressingDetails,
    totalCost,
    calories,
    quantity,
    modifications,
  } = useCustomizationContext({ actor });

  const {
    startCustomization,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
    changeDressingPortions,
    changeDressingWeight,
  } = useCustomizationHelpers({ actor });

  // ─────────────────────────────────────────────────────────────────────

  const { currentBreakpoint, minWidth, match } = useResponsive();
  const { formatMessage } = useIntl();

  const headerStyle = match([null, [styles.headerSM], styles.headerLG]);
  const headerText = formatMessage(messages.dressingDetailsTitle);

  // ─── Flags ───────────────────────────────────────────────────────────

  const cannotChangeDressingMode = !canMixDressings;

  const isMacronutrientsEnabled = useFeatureFlag('CELS-4323-order-macronutrients-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const customizationVariant = isNavbarV2Enabled ? 'new' : 'legacy';
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─── Context ─────────────────────────────────────────────────────────

  const dressingMode = useSelector(actor, (state) => {
    if (state.matches({ ready: { 'dressing-mode': 'mixed-in' } })) {
      return 'mixedIn';
    }

    if (state.matches({ ready: { 'dressing-mode': 'on-the-side' } })) {
      return 'onTheSide';
    }
  });

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────

  const selectOnTheSide = useCallback(() => {
    setDressingModeToOnTheSide();
    track('customization.dressing.on-the-side.tapped');
  }, [setDressingModeToOnTheSide, track]);

  const selectMixedIn = useCallback(() => {
    setDressingModeToMixedIn();
    track('customization.dressing.mixed-in.tapped');
  }, [setDressingModeToMixedIn, track]);

  // ─────────────────────────────────────────────────────────────────────

  if (!product) return null;

  return (
    <CustomizationContainer>
      <CustomizationDetailsCompoundContainer.Aside isFluid>
        <CustomizationDetailsCompoundContainer.ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          stickyHeaderIndices={[0]}
          onScroll={updateIngredientsScrollSV}
          scrollEventThrottle={16}
          bounces={false}
        >
          <CustomizationHeader
            spacing="standard"
            shouldAnimateHeaderContainer={minWidth.isSM}
            shouldAnimateHeaderText={minWidth.isSM}
            style={headerStyle}
            scrollOffsetSV={selectedIngredientsScrollSV}
            restaurantSlug={restaurantSlug}
            addressId={addressId}
            text={headerText}
            shouldDisableControls={shouldDisableControls}
            variant={customizationVariant}
            onBackButtonPress={startCustomization}
          />

          {product.isModifiable && dressingMode ? (
            <CustomizationDetails.Container>
              <CustomizationDetails.DressingMode
                dressingMode={dressingMode}
                maxDressingsPortions={maxDressingsPortionsNumber}
                hasNoSelectedDressing={hasNoSelectedDressing}
                isDressingModeChangeNotAllowed={cannotChangeDressingMode}
                isDelivery={isDelivery}
                isOutpost={isOutpost}
                isDisabled={shouldDisableControls}
                setDressingModeToOnTheSide={selectOnTheSide}
                setDressingModeToMixedIn={selectMixedIn}
              />

              <View style={styles.dressingsTitleContainer}>
                <CustomizationDetails.DressingsTitle
                  dressingMode={dressingMode}
                  hasNoSelectedDressing={hasNoSelectedDressing}
                  maxDressingsPortions={maxDressingsPortionsNumber}
                />

                {hasNoSelectedDressing ? (
                  <CustomizationDetails.AddDressing onPress={startCustomization} />
                ) : null}
              </View>

              {dressingsDetails.map((dressingDetails) => {
                const { modification, weight, portionsNumber } = dressingDetails;

                if (!modification.ingredient) return null;

                const { id, isMixable } = modification.ingredient;

                const remainingPortions =
                  maxDressingsPortionsNumber - activeDressingsPortionsNumber;
                const shouldRenderPortionVariation = dressingMode === 'onTheSide' || !isMixable;

                if (shouldRenderPortionVariation) {
                  return (
                    <CustomizationDetails.DressingPortionV2
                      key={id}
                      ingredient={modification.ingredient}
                      selectedPortion={portionsNumber}
                      maxPortions={maxDressingsPortionsNumber}
                      remainingPortions={remainingPortions}
                      isDisabled={shouldDisableControls}
                      restrictionModalTitle={customizationContent.fields?.restrictionModalTitle}
                      restrictionModalButtonTitle={
                        customizationContent.fields?.restrictionModalButtonTitle
                      }
                      setDressingPortion={changeDressingPortions}
                    />
                  );
                }

                return (
                  <CustomizationDetails.DressingWeightV2
                    key={id}
                    ingredient={modification.ingredient}
                    selectedWeight={weight ?? DressingWeight.Medium}
                    isDisabled={shouldDisableControls}
                    restrictionModalTitle={customizationContent.fields?.restrictionModalTitle}
                    restrictionModalButtonTitle={
                      customizationContent.fields?.restrictionModalButtonTitle
                    }
                    setDressingWeight={changeDressingWeight}
                  />
                );
              })}
            </CustomizationDetails.Container>
          ) : null}
        </CustomizationDetailsCompoundContainer.ScrollView>

        {minWidth.isSM && !isMacronutrientsEnabled ? (
          <CustomizationFloatingBar>
            <CustomizationCTA
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              variation={isEditProductMode ? 'update' : 'add-to-bag'}
              variant={customizationVariant}
              cost={totalCost}
              calories={calories}
              quantity={quantity}
              disabled={shouldDisableControls}
              onPress={completeCustomization}
            />
          </CustomizationFloatingBar>
        ) : null}
      </CustomizationDetailsCompoundContainer.Aside>

      {minWidth.isSM ? (
        <View style={styles.imageContainer}>
          <DressingProductImage
            baseUrl={dressingDetailsImage}
            isCustomizationActive={true}
            product={product}
          />

          {isMacronutrientsEnabled && modifications ? (
            <CustomizationBottomBar>
              <CustomizationNutritionCalculator.Floating
                calories={calories}
                activeModifications={modifications.active}
                mixedDressingDetails={mixedDressingDetails}
                product={product}
              />

              <CustomizationCTA
                variation={isEditProductMode ? 'update' : 'add-to-bag'}
                variant={customizationVariant}
                cost={totalCost}
                quantity={quantity}
                disabled={shouldDisableControls}
                onPress={completeCustomization}
              />
            </CustomizationBottomBar>
          ) : null}
        </View>
      ) : null}

      {currentBreakpoint.isXS ? (
        <CustomizationFloatingBar>
          {isMacronutrientsEnabled && modifications ? (
            <CustomizationNutritionCalculator.Floating
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              calories={calories}
              activeModifications={modifications.active}
              mixedDressingDetails={mixedDressingDetails}
              product={product}
              variant={customizationVariant === 'new' ? 'compact' : 'standard'}
            />
          ) : null}

          <CustomizationFloatingControls
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            variant={customizationVariant}
            shouldDisableControls={shouldDisableControls}
            onBackButtonPress={startCustomization}
          >
            <CustomizationCTA
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              variation={isEditProductMode ? 'update' : 'add-to-bag'}
              variant={customizationVariant}
              cost={totalCost}
              quantity={quantity}
              calories={isMacronutrientsEnabled ? undefined : calories}
              disabled={shouldDisableControls}
              onPress={completeCustomization}
            />
          </CustomizationFloatingControls>
        </CustomizationFloatingBar>
      ) : null}

      <CustomizationModals
        actor={actor}
        customizationContent={customizationContent}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        closeOutOfStockModal={closeOutOfStockModal}
      />
    </CustomizationContainer>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dressingDetailsTitle: {
    defaultMessage: 'Dressing details',
    description: 'Customization | Title | Dressing details',
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingBottom: theme.spacing['16'],
  },
  contentContainer: {
    flexGrow: 1,
  },
  imageContainer: {
    flex: 1,
  },
  headerSM: {
    paddingHorizontal: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
  },
  headerLG: {
    paddingHorizontal: theme.spacing['4'],
    paddingVertical: theme.spacing['1'],
  },
  dressingsTitleContainer: {
    gap: theme.spacing['4'],
    marginTop: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  customizationContent: ReturnType<typeof useCustomizationContent>;
  isOutpost: boolean;
  isEditProductMode: boolean;
  shouldDisableControls: boolean;
  addressId: string | undefined;
  isOutOfStockModalOpen: boolean;
  closeOutOfStockModal: () => void;
  completeCustomization: () => void;
};
